import React, {useEffect, useState} from 'react'
import compose from 'recompose/compose'
import { withI18n } from '../../hocs'
import PropTypes from 'prop-types'
import { ArrowRight } from 'lucide-react'
import * as S from './styled'
import {CustomAlert, RegularButton, InputField} from '../../atoms'
import {useMutation} from "@tanstack/react-query";
import {deleteTag} from "../../../api";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import ActionDialog, { LoadingLabel } from '../action-dialog'
import { tokens } from '../../../assets/tokens'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useAppContext } from '../../../context/appContext'
import { ALERT_LEVEL } from '../../../config/constants'

const ModalDeleteTag = ({ open, handleClose, i18n, tagId, tagName, handleOnClickDelete, deleteParams }) => {

  const [showAlert, setShowAlert] = useState(false);
  const [alert, setAlert] = useState({ message: '', severity: '' });
  const [targetTagValue, setTargetTagValue] = useState(null);
  const [isInUse, setIsInUse] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const { createAlert } = useAppContext()

  useEffect(() => {
    if (alert.message) {
      setShowAlert(true);
      const timer = setTimeout(() => {
        setShowAlert(false);
        setAlert({ message: '', severity: '' });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [alert]);

  useEffect(() => {
    if (deleteParams && deleteParams.ids && deleteParams.ids.length > 0) {
      setIsInUse(true);
    } else {
      setIsInUse(false);
    }
  }, [deleteParams]);

  const removeTag = async (data) => {
    try {
      const response = await deleteTag(data);
      return response.data;
    } catch (error) {
      console.error("Error while deleting tag:", error);
      setAlert({ message: i18n.tag_management.deleteTagError, severity: 'error' });
      throw error;
    }
  }

  const mutationDeleteTag = useMutation(removeTag);

  // Mientras la página comprueba si la etiqueta está en uso se muestra un spinner en el cuerpo
  const isChecking = !!(deleteParams && deleteParams.isChecking)
  const checkError = !!(deleteParams && deleteParams.checkError)

  // Se cierra cuando el borrado ha ido bien y la tabla ya está refrescada; si falla, el modal sigue abierto con el error
  const handleDeleteButton = async (targetTagToMove = null) => {
    try {
      if (tagId) {
        let data = {id: tagId}
        if (deleteParams && deleteParams.ids && deleteParams.ids.length > 0) {
          data.is_in_use = true;
          if (targetTagToMove && targetTagToMove.id && targetTagToMove.module) {
            data.target_tag_to_move = targetTagToMove;
          }
        }
        await mutationDeleteTag.mutateAsync(data);
        await handleOnClickDelete();
        handleClose();
        createAlert(i18n.tag_management.deleteTagSuccess, ALERT_LEVEL.SUCCESS);
      }
    } catch (error) {
      console.error("Error while deleting tag:", error);
      setAlert({message: i18n.tag_management.deleteTagError, severity: 'error'});
      setShowAlert(true);
    }
  }

  const moveTags = async () => {
    if (!targetTagValue) {
      setShowAlert(true);
      setAlert({message: i18n.tag_management.moveToEmpty, severity: 'error'});
    } else {
      setIsMoving(true);
      await handleDeleteButton(targetTagValue);
      setIsMoving(false);
    }
  }

  return (
    <ActionDialog
      open={open}
      title={`${i18n.tag_management.removeTag} ${tagName}`}
      onClose={() => !isMoving && handleClose()}
      onConfirm={() => handleDeleteButton()}
      cancelLabel={i18n.phases_states.buttonCancel}
      confirmLabel={isInUse ? i18n.tag_management.remove_anyways : i18n.tag_management.remove_confirmed}
      confirmColor="error"
      confirmDisabled={isMoving || isChecking || checkError}
      isPending={mutationDeleteTag.isLoading && !isMoving}
    >
      {isChecking ? (
        <S.Checking><CircularProgress size={24} /></S.Checking>
      ) : checkError ? (
        <S.Body><S.Text>{i18n.tag_management.checkInUseError}</S.Text></S.Body>
      ) : (
      <S.Body>
        <S.Text>
          {isInUse
            ? i18n.tag_management.tag_in_use.replace('{tag_name}', tagName)
            : i18n.tag_management.remove_tag_confirmation.replace('{tag_name}', tagName)
          }
        </S.Text>
        <S.MoveRow>
          <S.Field>
            <label>{i18n.tag_management.labelCurrentTag}</label>
            <InputField value={tagName} disabled={true} />
          </S.Field>
          {isInUse && <>
            <S.Arrow>
              <ArrowRight size={20} color={tokens.color.text.secondary} />
            </S.Arrow>
            <S.Field>
              <label>{i18n.tag_management.labelNewTag}</label>
              <Autocomplete
                id="change-tag"
                value={targetTagValue}
                options={deleteParams ? deleteParams.availableTags : []}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => <TextField {...params} variant="outlined" />}
                disableClearable={true}
                onChange={(event, newValue) => {
                  setTargetTagValue(newValue);
                }}
              />
            </S.Field>
            <RegularButton type="button" title={<LoadingLabel loading={isMoving}>{i18n.phases_states.buttonMove}</LoadingLabel>}
              disabled={mutationDeleteTag.isLoading} onClick={() => !isMoving && moveTags()} />
          </>}
        </S.MoveRow>
        {showAlert && <CustomAlert severity={alert.severity} message={alert.message} />}
      </S.Body>
      )}
    </ActionDialog>
  )
}

ModalDeleteTag.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  i18n: PropTypes.object.isRequired,
  tagName: PropTypes.string,
  handleOnClickDelete: PropTypes.func
}

const enharce = compose(withI18n)

export default enharce(ModalDeleteTag)
